import React, { useState } from "react";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { loadImageDimensions, urlToBase64 ,capitalizeFirstLetter, parseAmount} from "../modules/invoiceUtils";
import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

export  const generateInvPrmInvoicePdf = async (  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,) => {
      const { documentConfig, isInterState, isPreSale,hasPayment, } = invoiceConfig;
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    const centerX = pageW / 2;
    let y = PADDING; // Starting height
    const imagePath = documentConfig?.image;
    // 1. DRAW LOGO (Far Left) - Safely handle missing/invalid image
    let finalH = 0;
    if (imagePath) {
      try {
        const logoBase64 = await urlToBase64(imagePath);
        if (logoBase64) {
          const { width, height } = await loadImageDimensions(logoBase64);
          const maxWidth = 30;
          const maxHeight = 12;
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          const finalW = width * ratio;
          finalH = height * ratio;

          doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
        }
      } catch (imgError) {
        console.warn("Logo failed to load/render:", imgError);
        finalH = 0; // Fallback if image loading fails
      }
    }   
    try {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.text(documentConfig?.title || "", centerX, y + 3, { align: "center" });

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(
        documentConfig?.fromData?.title?.toUpperCase() || "",
        centerX,
        y + 8,
        { align: "center" }
      );

      doc.setFontSize(9);
      doc.setTextColor(0);

      // Invoice No Row
      const invLabel = documentConfig?.numberLabel || "";
      const invVal = documentConfig?.number || "-";
      const invDateTime = documentConfig?.Date?.time;
      const dateStr = `Date : ${documentConfig?.Date?.date || ""}`;
      const timeStr = `Time : ${invDateTime || ""}`;
      const invValW = doc.getTextWidth(invVal);

      doc.setFont("Inter-Regular", "normal");
      doc.text(
        invLabel,
        MARGIN_RIGHT - invValW - doc.getTextWidth(invLabel),
        y + 2
      );
      doc.setFont("Inter-Bold", "bold");
      doc.text(invVal, MARGIN_RIGHT, y + 2, { align: "right" });

      const totalDateTimeWidth =
        doc.getTextWidth(dateStr) + doc.getTextWidth(timeStr);

      const lineStartX = MARGIN_RIGHT - totalDateTimeWidth - 1;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || "#cccccc");
      doc.setLineWidth(0.1);
      doc.line(lineStartX, y + 4, MARGIN_RIGHT, y + 4);

      const timeStrW = doc.getTextWidth(timeStr);
      doc.setFont("Inter-Regular", "normal");
      doc.text(dateStr, MARGIN_RIGHT - timeStrW - 1, y + 8, { align: "right" });
      doc.text(timeStr, MARGIN_RIGHT, y + 8, { align: "right" });

      // --- ADDRESS & CONTACT SECTION FIX ---
      // If no image, start text baseline right at 'y', otherwise below logo height
      let currentTextY = finalH > 0 ? y + finalH + 4 : y + 3;

      const addrLine1 = [
        documentConfig?.fromData?.addressline1,
        documentConfig?.fromData?.addressline2,
      ]
        .filter(Boolean)
        .join(", ");

      const addrLine2 = [
        documentConfig?.fromData?.district,
        documentConfig?.fromData?.state,
        documentConfig?.fromData?.pincode,
      ]
        .filter(Boolean)
        .join(", ");

      const maxWidthAdd = 60;

      // Only render and offset Y if address line 1 exists
      if (addrLine1.trim()) {
        const wrappedAddress1 = doc.splitTextToSize(addrLine1, maxWidthAdd);
        doc.text(wrappedAddress1, MARGIN_LEFT, currentTextY);
        currentTextY += wrappedAddress1.length * 4;
      }

      // Only render and offset Y if address line 2 exists
      if (addrLine2.trim()) {
        const wrappedAddress2 = doc.splitTextToSize(addrLine2, maxWidthAdd);
        doc.text(wrappedAddress2, MARGIN_LEFT, currentTextY);
        currentTextY += wrappedAddress2.length * 4;
      }

      // Render Phone & GSTIN immediately at currentTextY
      let currentX = MARGIN_LEFT;
      const phone = `${
        documentConfig?.fromData?.phone ? documentConfig.fromData.phone : ""
      }${
        documentConfig?.fromData?.additionalPhone
          ? `,${documentConfig.fromData.additionalPhone}`
          : ""
      }`.trim();
      const gstin = documentConfig?.fromData?.gstnumber;

      if (phone || gstin) {
        if (phone) {
          doc.setFont("Inter-Bold", "bold");
          doc.text("Ph:", currentX, currentTextY);
          currentX += doc.getTextWidth("Ph: ");

          doc.setFont("Inter-Regular", "normal");
          doc.text(`${phone} `, currentX, currentTextY);
          currentX += doc.getTextWidth(`${phone} `);
        }

        if (gstin) {
          doc.setFont("Inter-Bold", "bold");
          doc.text("GSTIN:", currentX, currentTextY);
          currentX += doc.getTextWidth("GSTIN: ");

          doc.setFont("Inter-Regular", "normal");
          doc.text(`${gstin}`, currentX, currentTextY);
        }

        currentTextY += 4; // Add small spacing below contact row
      }

      y = currentTextY;
    } catch (e) {
      console.warn("Header layout failed", e);
      y += 15;
    } 

    if (documentConfig?.toData) {
      y=documentConfig?.image? y: y+=5 ;
      const hasShipping =
        documentConfig?.toData?.shippingAddress?.addressline1 ||
        documentConfig?.toData?.shippingAddress?.addressline2; // adjust key if needed

      let rowHeight = 6;
      doc.setDrawColor(
        selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
      );
      doc.setLineWidth(0.2);

      const totalBoxWidth = MARGIN_RIGHT - MARGIN_LEFT;
      const col1EndX = hasShipping
        ? MARGIN_LEFT + totalBoxWidth * 0.39
        : MARGIN_LEFT + totalBoxWidth * 0.75;
      const col2EndX = MARGIN_LEFT + totalBoxWidth * 0.76;
      const rightSectionStart = hasShipping ? col2EndX + 2 : col2EndX;

      // --- LEFT: Customer Details ---
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text(documentConfig?.toLabel, MARGIN_LEFT + 2, y + 4);

      doc.setFontSize(8);
      doc.setFont("Inter-Regular", "normal");
      doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT + 2, y + 8);
      rowHeight += 4;

      const fullAddress = [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.state,
      ]
        .filter(Boolean)
        .join(", ");
      const pinCode = documentConfig?.toData?.pincode
        ? ` - ${documentConfig?.toData.pincode}`
        : "";

      const col1MaxWidth = col1EndX - MARGIN_LEFT - 4;
      if (fullAddress || pinCode) {
        doc.setFontSize(9);
        const wrappedAddr = doc.splitTextToSize(
          fullAddress + pinCode,
          col1MaxWidth,
        );
        doc.text(wrappedAddr, MARGIN_LEFT + 2, y + 12);
        rowHeight += wrappedAddr.length * 3.5;
      }

      // --- MIDDLE: Shipping Details ---
      let shippingRowHeight = 6;
      if (hasShipping) {
        const shipping = documentConfig.toData?.shippingAddress;
        const col2StartX = col1EndX + 2;
        const col2MaxWidth = col2EndX - col1EndX - 4;

        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(9);
        doc.text("Shipping Details :", col2StartX, y + 4);

        doc.setFontSize(8);
        doc.setFont("Inter-Regular", "normal");
        const shipAddress = [
          shipping?.addressline1,
          shipping?.addressline2,
          shipping?.district,
          shipping?.state,
        ]
          .filter(Boolean)
          .join(", ");
        const shipPin = shipping?.pincode ? ` - ${shipping.pincode}` : "";

        if (shipAddress || shipPin) {
          doc.setFontSize(9);
          const wrappedShip = doc.splitTextToSize(
            shipAddress + shipPin,
            col2MaxWidth,
          );
          doc.text(wrappedShip, col2StartX, y + 8);
          shippingRowHeight += wrappedShip.length * 3.5;
        }

        rowHeight = Math.max(rowHeight, shippingRowHeight);
      }
      let detailY = y + 4;
      // --- RIGHT: Ph / GSTIN ---
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      const phLabel = "Ph: ";
      doc.text(phLabel, rightSectionStart, detailY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        documentConfig?.toData?.phone || "",
        rightSectionStart + doc.getTextWidth(phLabel),
        detailY,
      );
      detailY += 5;
      doc.setFont("Inter-Bold", "bold");
      const gstLabel = "GSTIN: ";
      doc.text(gstLabel, rightSectionStart, detailY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        documentConfig?.toData?.gstnumber || "",
        rightSectionStart + doc.getTextWidth(gstLabel),
        detailY,
      );
      if (documentConfig?.toData?.pannumber) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const panLabel = "PAN: ";
        doc.text(panLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.pannumber || "",
          rightSectionStart + doc.getTextWidth(panLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.fssai) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const fassiLabel = "FASSI: ";
        doc.text(fassiLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.fssai || "",
          rightSectionStart + doc.getTextWidth(fassiLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.cinnumber) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const cinLabel = "CIN: ";
        doc.text(cinLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.cinnumber || "",
          rightSectionStart + doc.getTextWidth(cinLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.referenceid) {
        detailY += 5;
        doc.setFont("Inter-Regular", "normal");
        const refidLabel = "Reference No: ";
        doc.text(refidLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.referenceid || "",
          rightSectionStart + doc.getTextWidth(refidLabel),
          detailY,
        );
        detailY += 5;
      }
      rowHeight = Math.max(rowHeight, detailY - y);
      // Draw dividers
      doc.line(col1EndX, y, col1EndX, y + rowHeight);
      if (hasShipping) {
        doc.line(col2EndX, y, col2EndX, y + rowHeight);
      }

      doc.rect(MARGIN_LEFT, y, MARGIN_RIGHT - MARGIN_LEFT, rowHeight);
      y += rowHeight;
    }
    /* -------------------------------------------------
        PRODUCT TABLE1
     ------------------------------------------------- */
    const rowCount = documentConfig?.items?.length;

    const standardHeight = 4;
    const targetTotalHeight = 40;
    const extraSpace =
      rowCount > 0 && rowCount <= 5
        ? targetTotalHeight - rowCount * standardHeight
        : 0;
    autoTable(doc, {
      startY: y,
      theme: "plain",
      rowPageBreak: "avoid",
      margin: {
        top: PADDING,
        left: PADDING,
        right: PADDING,
        bottom: PADDING,
      },
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 1.5,
        valign: "top",
        lineWidth: 0, // No default borders
      },
      headStyles: {
        font: "Inter-Bold",
        //  fontSize: 7,
        fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        lineWidth: 0.2,
        lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
      },
      bodyStyles: {
        minCellHeight: Math.max(2, standardHeight),
      },
      head: [["#", "Item", "Rate", "Qty", "Taxable Val", "Tax Amt", "Amount"]],
      body: documentConfig?.items.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,

        `${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${it.lineTotalBasePrice}`,
        `${it.lineTotalTaxAmount}`,

        `${it.lineGrandTotal}`,
      ]),
      // body:repeatedBody,
      columnStyles: {
        0: { cellWidth: 13, halign: "center" },
        1: { cellWidth: "auto", halign: "left" },
        2: { cellWidth: 25, halign: "right" },
        3: { cellWidth: 15, halign: "center" },
        4: { cellWidth: 25, halign: "right" },
        5: { cellWidth: 25, halign: "right" },
        6: { cellWidth: 25, halign: "right" },
      },
      didParseCell: function (data) {
         if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4,5,6].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
        if (data.section === "body") {
          data.cell.styles.lineWidth = {
            left: 0.1,
            right: 0.1,
            top: 0,
            bottom: 0,
          };
          data.cell.styles.lineColor = selectedTheme?.templateTheme
            ?.borderColor || [200, 200, 200]; // Black vertical lines
          if (data.row.index === rowCount - 1) {
            data.cell.styles.minCellHeight = standardHeight + extraSpace;
          }
          if (data.row.index === rowCount - 1) {
            data.cell.styles.lineWidth.bottom = 0.1;
          }
        }
      },
      didDrawPage: function (data) {
        const finalY = data.cursor.y;
        doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
        doc.line(PADDING, finalY, pageW - PADDING, finalY); // Bottom horizontal line
      },
    });

    let finalY = doc.lastAutoTable.finalY;
    const footerHeight = 45; // Increased to accommodate the stacked boxes

    if (finalY + footerHeight > pageH - PADDING) {
      doc.addPage();
      finalY = PADDING;
    }

    const borderColor = selectedTheme?.templateTheme?.borderColor || [
      200, 200, 200,
    ];
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.setLineWidth(0.1);

    // X-coordinate for the vertical divider (aligns with the last column)
    const dividerX = MARGIN_RIGHT - 50;
    const boxWidth = MARGIN_RIGHT - PADDING;
  
    // --- BOX 1: TOTALS AND TAXES ---
    const box1Height = isInterState ? 12 : 18;
    doc.rect(PADDING, finalY, boxWidth, box1Height); // Outer border
    doc.line(dividerX, finalY, dividerX, finalY + box1Height); // Vertical divider

    // Box 1 Content: In Words
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.text("In Words:", PADDING + 2, finalY + 5);
    doc.setFont("Inter-Regular", "normal");
    const words = documentConfig?.amountInWords || "";
    doc.text(words, PADDING + 18, finalY + 5, {
      maxWidth: dividerX - PADDING - 17,
    });

    const taxSummary = [
      { label: "TOTAL", value: documentConfig?.grandTotal },

      ...(isInterState
        ? [{ label: "IGST", value: documentConfig?.igstAmount || 0 }]
        : [
            { label: "CGST", value: documentConfig?.cgstAmount || 0 },
            { label: "SGST", value: documentConfig?.sgstAmount || 0 },
          ]),
    ];

    taxSummary.forEach((item, i) => {
      let itemY = finalY + 4 + i * 6;
      doc.text(item.label, dividerX + 2, itemY);
      doc.text(`${item.value}`, MARGIN_RIGHT - 2, itemY, { align: "right" });
      if (i < taxSummary.length - 1) {
        doc.line(dividerX, itemY + 2, MARGIN_RIGHT, itemY + 2); // Mini horizontal lines
      }
    });
    const box2Y = finalY + box1Height;
    const box2Height = 12;
    doc.rect(PADDING, box2Y, boxWidth, box2Height); // Outer border
    doc.line(dividerX, box2Y, dividerX, box2Y + box2Height); // Vertical divider

    if (hasPayment) {
      const paymentData = [
        { label: "Cash", val: documentConfig?.cashPaid },
        { label: "Card", val: documentConfig?.cardPaid },
        { label: "UPI", val: documentConfig?.upiPaid },
        { label: "Online", val: documentConfig?.onlinePaid },
      ].filter((p) => Math.abs(parseAmount(p.val)) > 0);

      // --- BOX 2: PAYMENTS AND PAYABLES ---

      // Box 2 Content: Payment Methods (Left Side)
      let payX = PADDING + 2;

      paymentData.forEach((pay, index) => {
        const col = index % 2; // 0 or 1 (2 per row)
        const row = Math.floor(index / 2); // row number
        const colWidth = 50; // horizontal spacing
        const rowHeight = 5;
        const x = payX + col * colWidth;
        const y = box2Y + 4 + row * rowHeight;
        const displayVal = `${(pay.val)}`;
        doc.setFont("Inter-Bold", "bold");
        doc.text(displayVal, x, y);

        const valWidth = doc.getTextWidth(displayVal);
        doc.setFont("Inter-Regular", "normal");
        doc.text(` Paid via ${pay.label}`, x + valWidth + 1, y);
      });
    }
  
    // Box 2 Content: Payables (Right Side)
    const payableSummary = [
      { label: "Amount Payable", value: documentConfig?.balanceDue, isBold: true },
      {
        label: "Amount Paid",
        value: documentConfig?.paidAmount,
        isBold: false,
      },
    ];

    payableSummary.forEach((item, i) => {
      let itemY = box2Y + 4 + i * 6;
      const textColor = selectedTheme?.templateTheme?.foregroundColor;
      if (i === 0) {
        const themeColor =
          selectedTheme?.templateTheme?.primaryColor || "#F0F5FF";
        doc.setFillColor(themeColor);
        doc.rect(
          dividerX + 0.5,
          itemY - 4,
          MARGIN_RIGHT - dividerX - 1,
          6,
          "F",
        );
        // Draw the bottom separator line
        doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
        doc.line(dividerX, itemY - 4, MARGIN_RIGHT, itemY - 4);
        doc.line(dividerX, itemY + 2, MARGIN_RIGHT, itemY + 2);
      }
      if (item.isBold) {
        doc.setTextColor(textColor);
        doc.setFont("Inter-Bold", "bold");
      } else {
        doc.setTextColor(0, 0, 0); // black
        doc.setFont("Inter-Regular", "normal");
      }
      doc.text(item.label, dividerX + 2, itemY);
      doc.text(`${item.value}`, MARGIN_RIGHT - 2, itemY, { align: "right" });
    });

    // ==============================
    // Bank + QR + Signature Section
    // ==============================

    let sectionY = box2Y + 4 + payableSummary.length * 6 + 4;

    const leftX = PADDING;
    const qrSize = 28;
    const qrX = (pageW - qrSize) / 2;
    const rightX = MARGIN_RIGHT;

    // ---------- Calculate Heights ----------
    let bankHeight = 0;

    if (documentConfig?.bankDetails) {
      bankHeight = 5; // title

      if (documentConfig?.bankDetails?.beneficiaryName)
        bankHeight += 4;

      if (documentConfig?.bankDetails?.accountNo)
        bankHeight += 4;

      if (documentConfig?.bankDetails?.ifsc) bankHeight += 4;

      if (storeSettings?.paymentSettings?.upiId) bankHeight += 4;
    }

    let qrHeight = qrCodeDataUrl ? qrSize : 0;

    let signHeight = storeSettings?.invoiceSettings?.signature ? 22 : 14;

    // Take tallest block
    const blockHeight = Math.max(bankHeight, qrHeight, signHeight);

    // Page break if required
    if (sectionY + blockHeight > pageH - PADDING) {
      doc.addPage();
      sectionY = PADDING;
    }

    // ==================================
    // Bank Details
    // ==================================

    let bankY = sectionY;

    if (documentConfig?.bankDetails) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Bank Details", leftX, bankY);
      bankY += 5;
      const bankDetails = [];
     
      if (documentConfig?.bankDetails?.accountNo) {
        bankDetails.push({
          label: "Account #",
          value: documentConfig.bankDetails.accountNo,
        });
      }

      if (documentConfig?.bankDetails?.ifsc) {
        bankDetails.push({
          label: "IFSC",
          value: documentConfig.bankDetails.ifsc,
        });
      }
      if (documentConfig?.bankDetails?.beneficiaryName) {
        bankDetails.push({
          label: "Name",
          value: documentConfig.bankDetails.beneficiaryName,
        });
      }
        if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Bank Name",
        value: documentConfig.bankDetails.bankName,
      });
    }
      if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Branch Name",
        value: documentConfig.bankDetails.branch,
      });
    }
      if (documentConfig?.upiId) {
        bankDetails.push({
          label: "UPI",
          value: documentConfig.upiId,
        });
      }

      const labelWidthOffset = 18;

      bankDetails.forEach((item) => {
        doc.setFont("Inter-Regular", "normal");
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(`${item.label} :`, leftX, bankY);
        doc.setFont("Inter-Regular", "normal");
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(String(item.value || ""), leftX + labelWidthOffset, bankY);

        bankY += 4.5;
      });

      doc.setTextColor(0, 0, 0);
    }

    if (qrCodeDataUrl) {
      doc.addImage(qrCodeDataUrl, "PNG", qrX, sectionY - 3, qrSize, qrSize);
    }

    let signBottom = sectionY;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);

    doc.text(`For ${documentConfig?.fromData?.title}`, rightX, sectionY, {
      align: "right",
    });

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;

        const base64 = await urlToBase64(signimg);

        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 25;
        const maxHeight = 10;

        const ratio = Math.min(maxWidth / width, maxHeight / height);

        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          rightX - imgW,
          sectionY + 4,
          imgW,
          imgH,
        );

        signBottom = sectionY + 4 + imgH;
      } catch {}
    } else {
      doc.line(rightX - 30, sectionY + 10, rightX, sectionY + 10);

      signBottom = sectionY + 10;
    }

    doc.setFont("Inter-Regular", "normal");
    doc.text("Authorized Signatory", rightX, signBottom + 5, {
      align: "right",
    });

    signBottom += 5;

    const sectionEndY = Math.max(bankY, sectionY + qrHeight, signBottom);

    if (documentConfig?.remarks) {
      let remarkStartY = sectionEndY + 3;

      const remarkLines = doc.splitTextToSize(
        documentConfig?.remarks.charAt(0).toUpperCase() + documentConfig?.remarks.slice(1),
        pageW - PADDING * 2 - 4,
      );

      const remarkHeight = 4 + remarkLines.length * 4;

      // Page break if needed
      if (remarkStartY + remarkHeight > pageH - PADDING) {
        doc.addPage();
        remarkStartY = PADDING;
      }

      // Heading
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Remark", PADDING, remarkStartY);

      // Border box
      const boxY = remarkStartY + 3;
      doc.setDrawColor(borderColor);
      // doc.rect(
      //   PADDING,
      //   boxY,
      //   pageW - PADDING * 2,
      //   remarkHeight,
      //   "S"
      // );

      // Remark text
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);

      let textY = boxY + 2;

      remarkLines.forEach((line) => {
        if (textY > pageH - PADDING) {
          doc.addPage();
          textY = PADDING;
        }
        doc.text(line, PADDING, textY);
        textY += 4;
      });

      // Move Terms below the remark
      sectionY = boxY + remarkHeight;
    } else {
      sectionY = sectionEndY;
    }
    let termsStartY = sectionY + 3;

    // 👉 Estimate terms height
    const termLines = doc.splitTextToSize(terms || "", pageW - PADDING * 2);
    const termsHeight = 10 + termLines.length * 4;

    // 👉 Page break BEFORE rendering
    if (termsStartY + termsHeight > pageH - PADDING) {
      doc.addPage();
      termsStartY = PADDING;
    }

    if (terms?.length > 0 && !isPreSale ) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", PADDING, termsStartY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);

      let currentY = termsStartY + 5;
      const termLines1 = doc.splitTextToSize(terms, pageW - PADDING * 2);

      termLines1.forEach((line) => {
        if (currentY > pageH - PADDING) {
          doc.addPage();
          currentY = PADDING;
        }
        doc.text(line, PADDING, currentY);
        currentY += 5;
      });

      termsStartY = currentY;
    }

    // 👉 Row 3 (Branding)
    if (storeSettings?.negobillBranding) {
      let brandingY = termsStartY + 6;

      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(10);

      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);
      doc.text(normalText, MARGIN_LEFT, brandingY);

      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, MARGIN_LEFT + normalW, brandingY);
    }
    return doc;
  };

  export const generateInvPrmBillingPdf = async (  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,) => {
      const { documentConfig, hasPayment, isPreSale, } = invoiceConfig;
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    const centerX = pageW / 2;
    let y = PADDING; // Starting height
    const imagePath =documentConfig?.image;
       let finalH = 0;
    if (imagePath) {
      try {
        const logoBase64 = await urlToBase64(imagePath);
        if (logoBase64) {
          const { width, height } = await loadImageDimensions(logoBase64);
          const maxWidth = 30;
          const maxHeight = 12;
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          const finalW = width * ratio;
          finalH = height * ratio;

          doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
        }
      } catch (imgError) {
        console.warn("Logo failed to load/render:", imgError);
        finalH = 0; // Fallback if image loading fails
      }
    }   
    try {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.text(documentConfig?.title || "", centerX, y + 3, { align: "center" });

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(
        documentConfig?.fromData?.title?.toUpperCase() || "",
        centerX,
        y + 8,
        { align: "center" }
      );

      doc.setFontSize(9);
      doc.setTextColor(0);

      // Invoice No Row
      const invLabel = documentConfig?.numberLabel || "";
      const invVal = documentConfig?.number || "-";
      const invDateTime = documentConfig?.Date?.time;
      const dateStr = `Date : ${documentConfig?.Date?.date || ""}`;
      const timeStr = `Time : ${invDateTime || ""}`;
      const invValW = doc.getTextWidth(invVal);

      doc.setFont("Inter-Regular", "normal");
      doc.text(
        invLabel,
        MARGIN_RIGHT - invValW - doc.getTextWidth(invLabel),
        y + 2
      );
      doc.setFont("Inter-Bold", "bold");
      doc.text(invVal, MARGIN_RIGHT, y + 2, { align: "right" });

      const totalDateTimeWidth =
        doc.getTextWidth(dateStr) + doc.getTextWidth(timeStr);

      const lineStartX = MARGIN_RIGHT - totalDateTimeWidth - 1;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || "#cccccc");
      doc.setLineWidth(0.1);
      doc.line(lineStartX, y + 4, MARGIN_RIGHT, y + 4);

      const timeStrW = doc.getTextWidth(timeStr);
      doc.setFont("Inter-Regular", "normal");
      doc.text(dateStr, MARGIN_RIGHT - timeStrW - 1, y + 8, { align: "right" });
      doc.text(timeStr, MARGIN_RIGHT, y + 8, { align: "right" });
  
      // --- ADDRESS & CONTACT SECTION FIX ---
      // If no image, start text baseline right at 'y', otherwise below logo height
      let currentTextY = finalH > 0 ? y + finalH + 4 : y + 3;

      const addrLine1 = [
        documentConfig?.fromData?.addressline1,
        documentConfig?.fromData?.addressline2,
      ]
        .filter(Boolean)
        .join(", ");

      const addrLine2 = [
        documentConfig?.fromData?.district,
        documentConfig?.fromData?.state,
        documentConfig?.fromData?.pincode,
      ]
        .filter(Boolean)
        .join(", ");

      const maxWidthAdd = 60;

      // Only render and offset Y if address line 1 exists
      if (addrLine1.trim()) {
        const wrappedAddress1 = doc.splitTextToSize(addrLine1, maxWidthAdd);
        doc.text(wrappedAddress1, MARGIN_LEFT, currentTextY);
        currentTextY += wrappedAddress1.length * 4;
      }

      // Only render and offset Y if address line 2 exists
      if (addrLine2.trim()) {
        const wrappedAddress2 = doc.splitTextToSize(addrLine2, maxWidthAdd);
        doc.text(wrappedAddress2, MARGIN_LEFT, currentTextY);
        currentTextY += wrappedAddress2.length * 4;
      }

      // Render Phone & GSTIN immediately at currentTextY
      let currentX = MARGIN_LEFT;
      const phone = `${
        documentConfig?.fromData?.phone ? documentConfig.fromData.phone : ""
      }${
        documentConfig?.fromData?.additionalPhone
          ? `,${documentConfig.fromData.additionalPhone}`
          : ""
      }`.trim();
      const gstin = documentConfig?.fromData?.gstnumber;

      if (phone || gstin) {
        if (phone) {
          doc.setFont("Inter-Bold", "bold");
          doc.text("Ph:", currentX, currentTextY);
          currentX += doc.getTextWidth("Ph: ");

          doc.setFont("Inter-Regular", "normal");
          doc.text(`${phone} `, currentX, currentTextY);
          currentX += doc.getTextWidth(`${phone} `);
        }

        if (gstin) {
          doc.setFont("Inter-Bold", "bold");
          doc.text("GSTIN:", currentX, currentTextY);
          currentX += doc.getTextWidth("GSTIN: ");

          doc.setFont("Inter-Regular", "normal");
          doc.text(`${gstin}`, currentX, currentTextY);
        }

        currentTextY += 4; // Add small spacing below contact row
      }

      y = currentTextY;
    } catch (e) {
      console.warn("Header layout failed", e);
      y += 15;
    } 
    if (documentConfig?.toData) {
      y=documentConfig?.image? y: y+=5 ;
      const hasShipping =
        documentConfig?.toData?.shippingAddress?.addressline1 ||
        documentConfig?.toData?.shippingAddress?.addressline2; // adjust key if needed

      let rowHeight = 6;
      doc.setDrawColor(
        selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
      );
      doc.setLineWidth(0.2);

      const totalBoxWidth = MARGIN_RIGHT - MARGIN_LEFT;
      const col1EndX = hasShipping
        ? MARGIN_LEFT + totalBoxWidth * 0.39
        : MARGIN_LEFT + totalBoxWidth * 0.75;
      const col2EndX = MARGIN_LEFT + totalBoxWidth * 0.76;
      const rightSectionStart = hasShipping ? col2EndX + 2 : col2EndX;

      // --- LEFT: Customer Details ---
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Customer Details :", MARGIN_LEFT + 2, y + 4);

      doc.setFontSize(8);
      doc.setFont("Inter-Regular", "normal");
      doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT + 2, y + 8);
      rowHeight += 4;

      const fullAddress = [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.state,
      ]
        .filter(Boolean)
        .join(", ");
      const pinCode = documentConfig?.toData?.pincode
        ? ` - ${documentConfig?.toData.pincode}`
        : "";

      const col1MaxWidth = col1EndX - MARGIN_LEFT - 4;
      if (fullAddress || pinCode) {
        doc.setFontSize(9);
        const wrappedAddr = doc.splitTextToSize(
          fullAddress + pinCode,
          col1MaxWidth,
        );
        doc.text(wrappedAddr, MARGIN_LEFT + 2, y + 12);
        rowHeight += wrappedAddr.length * 3.5;
      }

      // --- MIDDLE: Shipping Details ---
      let shippingRowHeight = 6;
      if (hasShipping) {
        const shipping = documentConfig?.toData?.shippingAddress;
        const col2StartX = col1EndX + 2;
        const col2MaxWidth = col2EndX - col1EndX - 4;

        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(9);
        doc.text("Shipping Details :", col2StartX, y + 4);

        doc.setFontSize(8);
        doc.setFont("Inter-Regular", "normal");
        const shipAddress = [
          shipping?.addressline1,
          shipping?.addressline2,
          shipping?.district,
          shipping?.state,
        ]
          .filter(Boolean)
          .join(", ");
        const shipPin = shipping?.pincode ? ` - ${shipping.pincode}` : "";

        if (shipAddress || shipPin) {
          doc.setFontSize(9);
          const wrappedShip = doc.splitTextToSize(
            shipAddress + shipPin,
            col2MaxWidth,
          );
          doc.text(wrappedShip, col2StartX, y + 8);
          shippingRowHeight += wrappedShip.length * 3.5;
        }

        rowHeight = Math.max(rowHeight, shippingRowHeight);
      }
      let detailY = y + 4;
      // --- RIGHT: Ph / GSTIN ---
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      const phLabel = "Ph: ";
      doc.text(phLabel, rightSectionStart, detailY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        documentConfig?.toData?.phone || "",
        rightSectionStart + doc.getTextWidth(phLabel),
        detailY,
      );
      detailY += 5;
      doc.setFont("Inter-Bold", "bold");
      const gstLabel = "GSTIN: ";
      doc.text(gstLabel, rightSectionStart, detailY);
      doc.setFont("Inter-Regular", "normal");
      doc.text(
        documentConfig?.toData?.gstnumber || "",
        rightSectionStart + doc.getTextWidth(gstLabel),
        detailY,
      );
      if (documentConfig?.toData?.pannumber) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const panLabel = "PAN: ";
        doc.text(panLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.pannumber || "",
          rightSectionStart + doc.getTextWidth(panLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.fssai) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const fassiLabel = "FASSI: ";
        doc.text(fassiLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.fssai || "",
          rightSectionStart + doc.getTextWidth(fassiLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.cinnumber) {
        detailY += 5;
        doc.setFont("Inter-Bold", "bold");
        const cinLabel = "CIN: ";
        doc.text(cinLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.cinnumber || "",
          rightSectionStart + doc.getTextWidth(cinLabel),
          detailY,
        );
      }
      if (documentConfig?.toData?.referenceid) {
        detailY += 5;
        doc.setFont("Inter-Regular", "normal");
        const refidLabel = "Reference No: ";
        doc.text(refidLabel, rightSectionStart, detailY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.toData?.referenceid || "",
          rightSectionStart + doc.getTextWidth(refidLabel),
          detailY,
        );
        detailY += 5;
      }
      rowHeight = Math.max(rowHeight, detailY - y);
      // Draw dividers
      doc.line(col1EndX, y, col1EndX, y + rowHeight);
      if (hasShipping) {
        doc.line(col2EndX, y, col2EndX, y + rowHeight);
      }

      doc.rect(MARGIN_LEFT, y, MARGIN_RIGHT - MARGIN_LEFT, rowHeight);
      y += rowHeight;
    }
    /* -------------------------------------------------
        PRODUCT TABLE1
     ------------------------------------------------- */
    const rowCount = documentConfig?.items?.length;

    const standardHeight = 4;
    const targetTotalHeight = 40;
    const extraSpace =
      rowCount > 0 && rowCount <= 5
        ? targetTotalHeight - rowCount * standardHeight
        : 0;
    autoTable(doc, {
      startY: y,
      theme: "plain",
      rowPageBreak: "avoid",
      margin: {
        top: PADDING,
        left: PADDING,
        right: PADDING,
        bottom: PADDING,
      },
      styles: {
        font: "Inter-Regular",
        fontSize: 9,
        cellPadding: 1.5,
        valign: "top",
        lineWidth: 0, // No default borders
      },
      headStyles: {
        font: "Inter-Bold",
        //  fontSize: 7,
        fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        lineWidth: 0.2,
        lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
      },
      bodyStyles: {
        minCellHeight: Math.max(2, standardHeight),
      },
      head: [["#", "Item", "Rate", "Qty", "Amount"]],
      body: documentConfig?.items.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,

        `${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${it.lineGrandTotal}`,
      ]),
      // body:repeatedBody,
      columnStyles: {
        0: { cellWidth: 13, halign: "center" },
        1: { cellWidth: "auto", halign: "left" },
        2: { cellWidth: 25, halign: "right" },
        3: { cellWidth: 15, halign: "center" },
        4: { cellWidth: 25, halign: "right" },
      },
      didParseCell: function (data) {
        if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4,5,6].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
        if (data.section === "body") {
          data.cell.styles.lineWidth = {
            left: 0.1,
            right: 0.1,
            top: 0,
            bottom: 0,
          };
          data.cell.styles.lineColor = selectedTheme?.templateTheme
            ?.borderColor || [200, 200, 200]; // Black vertical lines
          if (data.row.index === rowCount - 1) {
            data.cell.styles.minCellHeight = standardHeight + extraSpace;
          }
          if (data.row.index === rowCount - 1) {
            data.cell.styles.lineWidth.bottom = 0.1;
          }
        }
      },
      didDrawPage: function (data) {
        const finalY = data.cursor.y;
        doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
        doc.line(PADDING, finalY, pageW - PADDING, finalY); // Bottom horizontal line
      },
    });

    let finalY = doc.lastAutoTable.finalY;
    const footerHeight = 25; // Increased to accommodate the stacked boxes

    if (finalY + footerHeight > pageH - PADDING) {
      doc.addPage();
      finalY = PADDING;
    }

    const borderColor = selectedTheme?.templateTheme?.borderColor || [
      200, 200, 200,
    ];
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.setLineWidth(0.1);

    // X-coordinate for the vertical divider (aligns with the last column)
    const dividerX = MARGIN_RIGHT - 65;
    const boxWidth = MARGIN_RIGHT - PADDING;

    // --- BOX 1: TOTALS AND TAXES ---
    const box1Height = 6;
    doc.rect(PADDING, finalY, boxWidth, box1Height); // Outer border
    doc.line(dividerX, finalY, dividerX, finalY + box1Height); // Vertical divider

    // Box 1 Content: In Words
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.text("In Words:", PADDING + 2, finalY + 4);
    doc.setFont("Inter-Regular", "normal");
    const words = documentConfig?.amountInWords || "";
    doc.text(words, PADDING + 18, finalY + 4, {
      maxWidth: dividerX - PADDING - 17,
    });

    const taxSummary = [{ label: "TOTAL", value: documentConfig?.grandTotal }];

    taxSummary.forEach((item, i) => {
      let itemY = finalY + 4 + i * 6;
      doc.text(item.label, dividerX + 2, itemY);
      doc.text(`${item.value}`, MARGIN_RIGHT - 2, itemY, { align: "right" });
    });
    const box2Y = finalY + box1Height;
    const box2Height = 12;
    doc.rect(PADDING, box2Y, boxWidth, box2Height); // Outer border
    doc.line(dividerX, box2Y, dividerX, box2Y + box2Height); // Vertical divider

    if (hasPayment) {
      const paymentData = [
        { label: "Cash", val: documentConfig?.cashPaid },
        { label: "Card", val: documentConfig?.cardPaid },
        { label: "UPI", val: documentConfig?.upiPaid },
        { label: "Online", val: documentConfig?.onlinePaid },
      ].filter((p) => Math.abs(parseAmount(p.val)) > 0);
      // --- BOX 2: PAYMENTS AND PAYABLES ---

      // Box 2 Content: Payment Methods (Left Side)
      let payX = PADDING + 2;

      paymentData.forEach((pay, index) => {
        const col = index % 2; // 0 or 1 (2 per row)
        const row = Math.floor(index / 2); // row number
        const colWidth = 50; // horizontal spacing
        const rowHeight = 5;
        const x = payX + col * colWidth;
        const y = box2Y + 4 + row * rowHeight;
        const displayVal = `${(pay.val)}`;
        doc.setFont("Inter-Bold", "bold");
        doc.text(displayVal, x, y);

        const valWidth = doc.getTextWidth(displayVal);
        doc.setFont("Inter-Regular", "normal");
        doc.text(` Paid via ${pay.label}`, x + valWidth + 1, y);
      });
    }
    const paidamount =
      (documentConfig?.grandTotal || 0) - (documentConfig?.balanceDue || 0);

    // Box 2 Content: Payables (Right Side)
    const payableSummary = [
      { label: "Amount Payable", value: documentConfig?.balanceDue, isBold: true },
      {
        label: "Amount Paid",
        value: documentConfig?.paidAmount,
        isBold: false,
      },
    ];

    payableSummary.forEach((item, i) => {
      let itemY = box2Y + 4 + i * 6;
      const textColor = selectedTheme?.templateTheme?.foregroundColor;
      if (i === 0) {
        const themeColor =
          selectedTheme?.templateTheme?.primaryColor || "#F0F5FF";

        doc.setFillColor(themeColor);
        doc.setTextColor(textColor);
        doc.rect(
          dividerX + 0.5,
          itemY - 4,
          MARGIN_RIGHT - dividerX - 1,
          6,
          "F",
        );
        // Draw the bottom separator line
        doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
        doc.line(dividerX, itemY - 4, MARGIN_RIGHT, itemY - 4);
        doc.line(dividerX, itemY + 2, MARGIN_RIGHT, itemY + 2);
      }
      if (item.isBold) {
        doc.setTextColor(textColor);
        doc.setFont("Inter-Bold", "bold");
      } else {
        doc.setTextColor(0, 0, 0); // black
        doc.setFont("Inter-Regular", "normal");
      }

      doc.text(item.label, dividerX + 2, itemY);
      doc.text(`${item.value}`, MARGIN_RIGHT - 2, itemY, { align: "right" });
    });

    let sectionY = box2Y + 4 + payableSummary.length * 6 + 4;

    const leftX = PADDING;
    const qrSize = 28;
    const qrX = (pageW - qrSize) / 2;
    const rightX = MARGIN_RIGHT;

    // ---------- Calculate Heights ----------
    let bankHeight = 0;

    if (documentConfig?.bankDetails) {
      bankHeight = 5; // title

      if (documentConfig?.bankDetails?.beneficiaryName)
        bankHeight += 4;

      if (documentConfig?.bankDetails?.accountNo)
        bankHeight += 4;

      if (documentConfig?.bankDetails?.ifsc) bankHeight += 4;

      if (storeSettings?.paymentSettings?.upiId) bankHeight += 4;
    }

    let qrHeight = qrCodeDataUrl ? qrSize : 0;

    let signHeight = storeSettings?.invoiceSettings?.signature ? 22 : 14;

    // Take tallest block
    const blockHeight = Math.max(bankHeight, qrHeight, signHeight);

    // Page break if required
    if (sectionY + blockHeight > pageH - PADDING) {
      doc.addPage();
      sectionY = PADDING;
    }

    // ==================================
    // Bank Details
    // ==================================

    let bankY = sectionY;
    if (documentConfig?.bankDetails) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Bank Details", leftX, bankY);
      bankY += 5;
      const bankDetails = [];
    
      if (documentConfig?.bankDetails?.accountNo) {
        bankDetails.push({
          label: "Account #",
          value: storeSettings.paymentSettings.bankDetails.accountNo,
        });
      }

      if (documentConfig?.bankDetails?.ifsc) {
        bankDetails.push({
          label: "IFSC",
          value: storeSettings.paymentSettings.bankDetails.ifsc,
        });
      }
     if (documentConfig?.bankDetails?.beneficiaryName) {
        bankDetails.push({
          label: "Name",
          value: storeSettings.paymentSettings.bankDetails.beneficiaryName,
        });
      }
      if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Bank Name",
        value: documentConfig.bankDetails.bankName,
      });
    }
      if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Branch Name",
        value: documentConfig.bankDetails.branch,
      });
    }
      if (documentConfig?.upiId) {
        bankDetails.push({
          label: "UPI",
          value: documentConfig.upiId,
        });
      }

      const labelWidthOffset = 18;

      bankDetails.forEach((item) => {
        doc.setFont("Inter-Regular", "normal");
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(`${item.label} :`, leftX, bankY);
        doc.setFont("Inter-Regular", "normal");
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(String(item.value || ""), leftX + labelWidthOffset, bankY);

        bankY += 4.5;
      });

      doc.setTextColor(0, 0, 0);
    }
    if (qrCodeDataUrl) {
      doc.addImage(qrCodeDataUrl, "PNG", qrX, sectionY - 3, qrSize, qrSize);
    }

    let signBottom = sectionY;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);

    doc.text(`For ${documentConfig?.fromData?.title}`, rightX, sectionY, {
      align: "right",
    });

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;

        const base64 = await urlToBase64(signimg);

        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 25;
        const maxHeight = 10;

        const ratio = Math.min(maxWidth / width, maxHeight / height);

        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          rightX - imgW,
          sectionY + 4,
          imgW,
          imgH,
        );

        signBottom = sectionY + 4 + imgH;
      } catch {}
    } else {
      doc.line(rightX - 30, sectionY + 10, rightX, sectionY + 10);

      signBottom = sectionY + 10;
    }

    doc.setFont("Inter-Regular", "normal");
    doc.text("Authorized Signatory", rightX, signBottom + 5, {
      align: "right",
    });

    signBottom += 5;

    const sectionEndY = Math.max(bankY, sectionY + qrHeight, signBottom);

    // 👉 Remark
    // let remarkStartY = signatureY + 4;

    if (documentConfig?.remarks) {
      let remarkStartY = sectionEndY + 3;

      const remarkLines = doc.splitTextToSize(
        documentConfig?.remarks.charAt(0).toUpperCase() + documentConfig?.remarks.slice(1),
        pageW - PADDING * 2 - 4,
      );

      const remarkHeight = 4 + remarkLines.length * 4;

      // Page break if needed
      if (remarkStartY + remarkHeight > pageH - PADDING) {
        doc.addPage();
        remarkStartY = PADDING;
      }

      // Heading
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Remark", PADDING, remarkStartY);

      // Border box
      const boxY = remarkStartY + 3;
      doc.setDrawColor(borderColor);
      // Remark text
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);

      let textY = boxY + 2;

      remarkLines.forEach((line) => {
        if (textY > pageH - PADDING) {
          doc.addPage();
          textY = PADDING;
        }
        doc.text(line, PADDING, textY);
        textY += 4;
      });

      // Move Terms below the remark
      sectionY = boxY + remarkHeight;
    } else {
      sectionY = sectionEndY;
    }
    // 👉 Row 2 (Terms)
    let termsStartY = sectionY + 3;

    const termLines = doc.splitTextToSize(terms || "", pageW - PADDING * 2);
    const termsHeight = 10 + termLines.length * 4;

    // 👉 Page break BEFORE rendering
    if (termsStartY + termsHeight > pageH - PADDING) {
      doc.addPage();
      termsStartY = PADDING + 1;
    }

    if (terms?.length > 0 && !isPreSale ) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", PADDING, termsStartY);
      doc.setFont("Inter-Regular", "normal");
      const termLines1 = doc.splitTextToSize(terms, pageW - PADDING * 2);

      let currentY = termsStartY + 5;

      termLines1.forEach((line) => {
        if (currentY > pageH - PADDING) {
          doc.addPage();
          currentY = PADDING;
        }

        doc.text(line, PADDING, currentY);
        currentY += 5;
      });

      termsStartY = currentY;
    }

    // 👉 Row 3 (Branding)
    if (storeSettings?.negobillBranding) {
      let brandingY = termsStartY + 6;

      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(10);

      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);
      doc.text(normalText, MARGIN_LEFT, brandingY);

      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, MARGIN_LEFT + normalW, brandingY);
    }
    return doc;
  };
  
const PrimeInvoice = ({
  globalData,
  terms,
  qrCodeDataUrl,  
  selectedTheme,
  invoiceConfig,
}) => {
   const {
      documentConfig,      
      showTaxDetails,
      isPreSale,
      showBillDetails,      
      isInterState,
      hasPayment
    } = invoiceConfig;
  
    const { storeSettings } = usePermission();
  return (
    <>
     
            {showTaxDetails ? (
              <>
             
                  <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-3">
                          <div className="d-flex pb-1">
                            <div style={{ maxWidth: "16rem" }}>
                              <div>
                                {documentConfig?.image && (
                                  <img
                                    src={
                                      documentConfig?.image
                                    }
                                    className="img-rounded logo mb-0 obj-contain"
                                    height="45"
                                    width="100"
                                  />
                                )}
                              </div>

                              <div className="font-12">
                                {documentConfig?.fromData?.addressline1}{" "}
                                {documentConfig?.fromData?.addressline2}
                              </div>
                              <div className="font-12">
                                {documentConfig?.fromData?.district}{" "}
                                {documentConfig?.fromData?.state || ""}{" "}
                                {documentConfig?.fromData?.pincode || ""}
                              </div>
                              <div className="font-12">
                                <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.fromData?.phone || ""}
                                     {documentConfig?.fromData?.additionalPhone && (
                                          <>
                                            ,{documentConfig.fromData.additionalPhone}
                                          </>
                                        )}{" "}
                                <span className="fw-bold">GSTIN:</span>{" "}
                                {documentConfig?.fromData?.gstnumber}
                              </div>
                            </div>

                            <div style={{ flex: "1" }} className="text-center">
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#555",
                                }}
                              >
                                {documentConfig?.title}
                              </div>
                              <div
                                className="font012 mt-0"
                                style={{
                                  fontWeight: "800",
                                  textTransform: "uppercase",
                                  lineHeight: "1",
                                }}
                              >
                                {documentConfig?.fromData?.title || "COMPANY NAME"}
                              </div>
                            </div>

                            <div style={{ flex: "1" }} className="text-end">
                              <div className="text-end d-inline-block">
                                <div
                                  className=" font-12 mb-0"
                                  style={{
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                 {documentConfig?.numberLabel}
                                  <span className="fw-bold">
                                    {" "}
                                    {documentConfig?.number}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-end font-12">
                                  {/* <span>DATE: {saleTime?.date}</span> */}
                                  <span> DATE: {documentConfig?.Date?.date}</span>
                                  <span className="ms-3">
                                    TIME:{documentConfig?.Date?.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {documentConfig?.toData && (
                            <div
                              className="d-flex "
                              style={{
                                border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              <div
                                style={{
                                  flex: "1",
                                  padding: "5px",
                                  borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                }}
                              >
                                <span className="fw-bold font-12">
                                  Customer Details :
                                </span>
                                <div className="font-12 ">
                                  {documentConfig?.toData?.name}
                                </div>
                                <div className="font-12">
                                  {" "}
                                  {documentConfig?.toData?.addressline1 &&
                                    `${documentConfig?.toData?.addressline1}, `}
                                  {documentConfig?.toData?.addressline2 &&
                                    `${documentConfig?.toData?.addressline2}, `}
                                  {documentConfig?.toData?.district &&
                                    `${documentConfig?.toData?.district}, `}
                                  {documentConfig?.toData?.pincode &&
                                    ` - ${documentConfig?.toData?.pincode}`}
                                </div>
                              </div>
                              {documentConfig?.toData?.shippingAddress
                                ?.addressline1 &&
                                documentConfig?.toData?.shippingAddress
                                  ?.addressline1 && (
                                  <div
                                    style={{
                                      flex: "1",
                                      padding: "5px",
                                      borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    }}
                                  >
                                    <span className="fw-bold font-12">
                                      shipping Details:
                                    </span>
                                    <div className="font-12 ">
                                      {
                                        documentConfig?.toData?.shippingAddress
                                          ?.name
                                      }
                                    </div>
                                    <div className="font-12">
                                      {" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline1 &&
                                        `${documentConfig?.toData?.shippingAddress?.addressline1}, `}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline2 &&
                                        `${documentConfig?.toData?.shippingAddress?.addressline2}, `}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.district &&
                                        `${documentConfig?.toData?.shippingAddress?.district}, `}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.pincode &&
                                        ` - ${documentConfig?.toData?.shippingAddress?.pincode}`}
                                    </div>
                                  </div>
                                )}
                              <div
                                style={{
                                  width: "200px",
                                  padding: "5px",
                                  WebkitFontSmoothing: "antialiased",
                                  textRendering: "optimizeLegibility",
                                }}
                              >
                                <div className="font-12">
                                  <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.toData?.phone || ""}
                                </div>
                                <div className=" font-12">
                                  {" "}
                                  <span className="fw-bold">GSTIN:</span>{" "}
                                  {documentConfig?.toData?.gstnumber || ""}
                                </div>
                                {documentConfig?.toData?.pannumber && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">PAN:</span>{" "}
                                    {documentConfig?.toData?.pannumber || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.fssai && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">FASSI:</span>{" "}
                                    {documentConfig?.toData?.fssai || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.cinnumber && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">CIN:</span>{" "}
                                    {documentConfig?.toData?.cinnumber || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.referenceid && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="">Reference No:</span>{" "}
                                    {documentConfig?.toData?.referenceid || ""}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <table
                            width="100%"
                            className="mt-0"
                            style={{ borderCollapse: "collapse" }}
                          >
                            <thead>
                              <tr className=" fw-bold">
                                <th
                                  className="force-style font-12 p-0 text-center"
                                  style={{
                                    width: "40px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                                >
                                  {" "}
                                  #
                                </th>
                                <th
                                  className="force-style font-12 p-0 ps-1"
                                  style={{
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "80px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                                >
                                  Rate
                                </th>
                                <th
                                  className="force-style font-12 p-0 pe-1 "
                                  style={{
                                    width: "60px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                                >
                                  Qty
                                </th>
                                <th
                                  // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "90px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                >
                                  Taxable Val
                                </th>
                                <th
                                  // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "90px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                >
                                  {" "}
                                  Tax Amt
                                </th>

                                <th
                                  // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            {documentConfig?.items?.length > 0 && (
                              <tbody>
                                {documentConfig?.items?.map((item, index) => (
                                  <tr
                                    key={index}
                                    className="font-12 align-top"
                                    style={{ height: "30px" }}
                                  >
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                                        textAlign: "center",
                                        borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    >
                                      {index + 1}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        padding: "0 5px",
                                      }}
                                    >
                                      {item?.title}
                                      {item.hsnsac && (
                                        <div className="font-12">
                                          <span className="fw-medium">
                                            {" "}
                                            {item.imode === "SERVICE"
                                              ? "SAC:"
                                              : "HSN:"}
                                          </span>{" "}
                                          {item.hsnsac}
                                        </div>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.baseprice}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "center",
                                      }}
                                    >
                                      {item?.quantity}
                                      <span className="font-11 ms-1">
                                        {(() => {
                                          const unitCode = item?.isSecondaryUnit
                                            ? item?.unitConfig
                                                ?.secondaryUnitCode
                                            : item?.unitConfig?.primaryUnitCode;

                                          // return unitCode !== "unit" ? unitCode : "";
                                          return unitCode || "";
                                        })()}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.lineTotalBasePrice}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.lineTotalTaxAmount}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.lineGrandTotal}
                                    </td>
                                  </tr>
                                ))}

                                {[
                                  ...Array(
                                    Math.max(
                                      0,
                                      5 - (documentConfig?.items?.length || 0),
                                    ),
                                  ),
                                ].map((_, i) => (
                                  <tr
                                    key={`empty-${i}`}
                                    style={{ height: "50px" }}
                                  >
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                  </tr>
                                ))}
                              </tbody>
                            )}
                            <tfoot
                              style={{
                                borderTop: `2px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              <tr className="font-11">
                                <td
                                  colSpan="5"
                                  style={{
                                    padding: "5px",
                                    verticalAlign: "top",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <span className="fw-bold">In Words:</span>{" "}
                                  {documentConfig?.amountInWords}
                                </td>

                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "0",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between p-0 px-2  font-12"
                                    style={{
                                      borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    }}
                                  >
                                    <span>Total</span>
                                    <span className="">
                                      {documentConfig?.grandTotal}
                                    </span>
                                  </div>
                                  {!isInterState ? (
                                    <>
                                      <div
                                        className="d-flex justify-content-between p-0 px-2  font-12"
                                        style={{
                                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        }}
                                      >
                                        <span>CGST</span>
                                        <span className="">
                                          {documentConfig?.cgstAmount || 0}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between p-0 px-2 font-12">
                                        <span>SGST</span>
                                        <span className="">
                                          {documentConfig?.sgstAmount || 0}
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="d-flex justify-content-between p-0 px-2 font-12 ">
                                      <span>IGST</span>
                                      <span className="">
                                        {documentConfig?.igstAmount || 0}
                                      </span>
                                    </div>
                                  )}
                                </td>
                              </tr>

                              {/* ROW 2: PAYMENT STATUS & SIGNATURE */}
                              <tr className="font-12">
                                {/* Left Side: Payment Methods (Cash/UPI/Card) */}
                                <td
                                  colSpan="5"
                                  style={{
                                    padding: "0px",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  {hasPayment && (
                                    <div className="row g-2 m-0">
                                      {parseAmount(documentConfig?.cashPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.cashPaid}</b> Paid
                                            via Cash
                                          </span>
                                        </div>
                                      )}
                                     
                                      {parseAmount(documentConfig?.cardPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.cardPaid}</b> Paid
                                            via Card
                                          </span>
                                        </div>
                                      )}
                                       {parseAmount(documentConfig?.upiPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.upiPaid}</b> Paid
                                            via UPI
                                          </span>
                                        </div>
                                      )}
                                      {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.onlinePaid}</b> Paid
                                            via Online
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Right Side Stack: Payment Summary */}
                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "0",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between p-0 px-2 border-bottom "
                                    style={{
                                      backgroundColor:
                                        selectedTheme?.templateTheme
                                          ?.primaryColor,
                                    }}
                                  >
                                    <span
                                      className="fw-bold font-12 "
                                      style={{
                                        color:
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      Amount Payable
                                    </span>
                                    <span
                                      className="fw-bold font-12 "
                                      style={{
                                        color:
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      {documentConfig?.balanceDue || 0}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between p-0 px-2">
                                    <span className="font-12">Amount Paid</span>
                                    <span className="font-12 ">
                                      
                                      {documentConfig?.paidAmount}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                          <div className="row mt-2">
                            <div className="col-4 p-2 pt-0  border-dark font-11">
                              {documentConfig?.bankDetails &&(
                                    <>
                            <strong>Bank Details:</strong>                            
                              {documentConfig?.bankDetails?.accountNo && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Account #:</span>
                                  {documentConfig.bankDetails.accountNo}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.ifsc && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>IFSC:</span>
                                  {documentConfig.bankDetails.ifsc}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.beneficiaryName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Name:</span>
                                  {documentConfig.bankDetails.beneficiaryName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.bankName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Bank Name:</span>
                                  {documentConfig.bankDetails.bankName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.branch && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Branch Name:</span>
                                  {documentConfig.bankDetails.branch}
                                </div>
                              )}

                              {documentConfig?.upiId && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>UPI ID:</span>
                                  {documentConfig?.upiId}
                                </div>
                              )}
                          </>
                                )}
                            </div>
                            {qrCodeDataUrl &&(
                              <div className="col-3 d-flex align-items-center justify-content-end">
                                <img
                                  src={qrCodeDataUrl}
                                  alt="QR"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                  }}
                                />
                              </div>
                            )}
                            <div className="col-4  ms-auto">
                              <div className="text-end p-0 fw-bold font-12 mt-2 ">
                                {documentConfig?.fromData?.title}
                              </div>
                              <div>
                                {storeSettings?.invoiceSettings?.signature ? (
                                  <div className="text-end p-0 fw-bold pb-1">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={100}
                                    />
                                  </div>
                                ) : (
                                  <div className="p-0 pt-4">
                                    <hr
                                      className="m-1 ms-auto "
                                      style={{
                                        border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        maxWidth: "120px",
                                      }}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0 font-12">
                                  Authorized Signatory
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              {documentConfig?.remarks && (
                                <table width="100%" className="mb-2">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div className="font-12 fw-600 align-left text-black mb-1">
                                          Remark
                                        </div>

                                        <div
                                          className="font-12 p-0"
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            minHeight: "30px",
                                          }}
                                        >
                                          {capitalizeFirstLetter(
                                            documentConfig?.remarks,
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>

                            <div className="col-12 ">
                              {terms?.length > 0 && !isPreSale && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td
                                        style={{
                                          padding: "0px",
                                        }}
                                        width="100%"
                                      >
                                        <div
                                          className="font-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-12">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <>
                              <div className="mt-2 font-11 d-flex justify-content-left ">
                                <span>
                                  Powered By{" "}
                                  <span className="fw-bold">NegoBill.com</span>
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
             
              </>
            ) : showBillDetails? (
              <>
                
                  <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-3">
                          <div className="d-flex pb-1">
                            <div style={{ maxWidth: "16rem" }}>
                              <div>
                                {documentConfig?.image && (
                                  <img
                                    src={
                                      documentConfig?.image
                                    }
                                    className="img-rounded logo mb-0 obj-contain"
                                    height="45"
                                    width="100"
                                  />
                                )}
                              </div>

                              <div className="font-12">
                                {documentConfig?.fromData?.addressline1}{" "}
                                {documentConfig?.fromData?.addressline2}
                              </div>
                              <div className="font-12">
                                {documentConfig?.fromData?.district}{" "}
                                {documentConfig?.fromData?.state || ""}{" "}
                                {documentConfig?.fromData?.pincode || ""}
                              </div>
                              <div className="font-12">
                                <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.fromData?.phone || ""}
                                     {documentConfig?.fromData?.additionalPhone && (
                                          <>
                                            , {documentConfig.fromData.additionalPhone}
                                          </>
                                        )}{" "}
                              </div>
                            </div>

                            <div
                              style={{
                                flex: "1",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#555",
                                }}
                              >
                                BILL RECEIPT
                              </div>
                              <div
                                className="font012 mt-0"
                                style={{
                                  fontWeight: "800",
                                  textTransform: "uppercase",
                                  lineHeight: "1",
                                }}
                              >
                                {documentConfig?.fromData?.title || "COMPANY NAME"}
                              </div>
                            </div>

                            <div
                              style={{
                                flex: "1",
                                textAlign: "right",
                              }}
                            >
                              <div
                                style={{
                                  display: "inline-block",
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  className=" font-12 mb-0"
                                  style={{
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                   {documentConfig?.numberLabel}
                                  <span className="fw-bold">
                                    {" "}
                                    {documentConfig?.number}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-end font-12">
                                  {/* <span>DATE: {saleTime?.date}</span> */}
                                  <span> {"Date :"} {documentConfig?.Date?.date}</span>
                                  <span className="ms-3">
                                   {documentConfig?.timeLabel ||"Time :"} {documentConfig?.Date?.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {documentConfig?.toData && (
                            <div
                              className="d-flex "
                              style={{
                                border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              <div
                                style={{
                                  flex: "1",
                                  padding: "5px",
                                  borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                }}
                              >
                                <span className="fw-bold font-12">
                                  Customer Details :
                                </span>
                                <div className="font-12 ">
                                  {documentConfig?.toData?.name}
                                </div>
                                <div className="font-12">
                                  {" "}
                                  {documentConfig?.toData?.addressline1 &&
                                    `${documentConfig?.toData?.addressline1}, `}
                                  {documentConfig?.toData?.addressline2 &&
                                    `${documentConfig?.toData?.addressline2}, `}
                                  {documentConfig?.toData?.district &&
                                    `${documentConfig?.toData?.district}, `}
                                  {documentConfig?.toData?.pincode &&
                                    ` - ${documentConfig?.toData?.pincode}`}
                                </div>
                              </div>
                              {(documentConfig?.toData?.shippingAddress
                                ?.addressline1 ||
                                documentConfig?.toData?.shippingAddress
                                  ?.addressline1) && (
                                <div
                                  style={{
                                    flex: "1",
                                    padding: "5px",
                                    borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <span className="fw-bold font-12">
                                    shipping Details:
                                  </span>
                                  <div className="font-12">
                                    {" "}
                                    {documentConfig?.toData?.shippingAddress
                                      ?.addressline1 &&
                                      `${documentConfig?.toData?.shippingAddress?.addressline1}, `}
                                    {documentConfig?.toData?.shippingAddress
                                      ?.addressline2 &&
                                      `${documentConfig?.toData?.shippingAddress?.addressline2}, `}
                                    {documentConfig?.toData?.shippingAddress
                                      ?.district &&
                                      `${documentConfig?.toData?.shippingAddress?.district}, `}
                                    {documentConfig?.toData?.shippingAddress
                                      ?.pincode &&
                                      ` - ${documentConfig?.toData?.shippingAddress?.pincode}`}
                                  </div>
                                </div>
                              )}
                              <div
                                style={{
                                  width: "180px",
                                  padding: "5px",
                                  WebkitFontSmoothing: "antialiased",
                                  textRendering: "optimizeLegibility",
                                }}
                              >
                                <div className="font-12">
                                  <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.toData?.phone || ""}
                                </div>
                                <div className=" font-12">
                                  {" "}
                                  <span className="fw-bold">GSTIN:</span>{" "}
                                  {documentConfig?.toData?.gstnumber || ""}
                                </div>
                                {documentConfig?.toData?.pannumber && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">PAN:</span>{" "}
                                    {documentConfig?.toData?.pannumber || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.fssai && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">FASSI:</span>{" "}
                                    {documentConfig?.toData?.fssai || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.cinnumber && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="fw-bold">CIN:</span>{" "}
                                    {documentConfig?.toData?.cinnumber || ""}
                                  </div>
                                )}
                                {documentConfig?.toData?.referenceid && (
                                  <div className=" font-12">
                                    {" "}
                                    <span className="">Reference No:</span>{" "}
                                    {documentConfig?.toData?.referenceid || ""}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <table
                            width="100%"
                            className="mt-0"
                            style={{ borderCollapse: "collapse" }}
                          >
                            <thead>
                              <tr className=" fw-bold">
                                <th
                                  className="force-style font-12 p-0 text-center"
                                  style={{
                                    width: "40px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                                >
                                  {" "}
                                  #
                                </th>
                                <th
                                  className="force-style font-12 p-0 ps-1"
                                  style={{
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                                >
                                  Rate
                                </th>
                                <th
                                  className="force-style font-12 p-0 pe-1 "
                                  style={{
                                    width: "60px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                  //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                                >
                                  Qty
                                </th>
                                <th
                                  // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                                  className="force-style font-12 p-0 pe-1"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                    "--th-bg":
                                      selectedTheme?.templateTheme
                                        ?.primaryColor,
                                    "--th-color":
                                      selectedTheme?.templateTheme
                                        ?.foregroundColor,
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            {documentConfig?.items?.length > 0 && (
                              <tbody>
                                {documentConfig?.items?.map((item, index) => (
                                  <tr
                                    key={index}
                                    className="font-12 align-top"
                                    style={{ height: "30px" }}
                                  >
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                                        textAlign: "center",
                                        borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    >
                                      {index + 1}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        padding: "0 5px",
                                      }}
                                    >
                                      {item?.title}
                                      {item.hsnsac && (
                                        <div className="font-12">
                                          <span className="fw-medium">
                                            {" "}
                                            {item.imode === "SERVICE"
                                              ? "SAC:"
                                              : "HSN:"}
                                          </span>{" "}
                                          {item.hsnsac}
                                        </div>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.baseprice}
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "center",
                                      }}
                                    >
                                      {item?.quantity}
                                      <span className="font-11 ms-1">
                                        {(() => {
                                          const unitCode = item?.isSecondaryUnit
                                            ? item?.unitConfig
                                                ?.secondaryUnitCode
                                            : item?.unitConfig?.primaryUnitCode;

                                          // return unitCode !== "unit" ? unitCode : "";
                                          return unitCode || "";
                                        })()}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        textAlign: "right",
                                        paddingRight: "5px",
                                      }}
                                    >
                                      {item?.lineGrandTotal}
                                    </td>
                                  </tr>
                                ))}

                                {[
                                  ...Array(
                                    Math.max(
                                      0,
                                      5 - (documentConfig?.items?.length || 0),
                                    ),
                                  ),
                                ].map((_, i) => (
                                  <tr
                                    key={`empty-${i}`}
                                    style={{ height: "50px" }}
                                  >
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                      }}
                                    ></td>
                                  </tr>
                                ))}
                              </tbody>
                            )}
                            <tfoot
                              style={{
                                borderTop: `2px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              }}
                            >
                              <tr className="font-11">
                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "1px",
                                    verticalAlign: "top",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <span className="fw-bold ps-1 font-12">
                                    In Words:
                                  </span>{" "}
                                  {documentConfig?.amountInWords}
                                </td>

                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "0",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <div className="d-flex justify-content-between p-0 px-2  font-12">
                                    <span>Total</span>
                                    <span className="">
                                      {documentConfig?.grandTotal}
                                    </span>
                                  </div>
                                </td>
                              </tr>

                              {/* ROW 2: PAYMENT STATUS & SIGNATURE */}
                              <tr className="font-12">
                                {/* Left Side: Payment Methods (Cash/UPI/Card) */}
                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "0px",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  {" "}
                                  {hasPayment && (
                                    <div className="row g-2 m-0">
                                      {parseAmount(documentConfig?.cashPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.cashPaid}</b> Paid
                                            via Cash
                                          </span>
                                        </div>
                                      )}
                                      
                                      {parseAmount(documentConfig?.cardPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.cardPaid}</b> Paid
                                            via Card
                                          </span>
                                        </div>
                                      )}
                                      {parseAmount(documentConfig?.upiPaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.upiPaid}</b> Paid
                                            via UPI
                                          </span>
                                        </div>
                                      )}
                                      {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                        <div className="col-6 font-12 ps-1">
                                          <span>
                                            <b>{documentConfig?.onlinePaid}</b> Paid
                                            via Online
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Right Side Stack: Payment Summary */}
                                <td
                                  colSpan="3"
                                  style={{
                                    padding: "0",
                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between p-0 px-2  "
                                    style={{
                                      backgroundColor: `${
                                        selectedTheme?.templateTheme
                                          ?.primaryColor
                                      }`,
                                    }}
                                  >
                                    <span
                                      className="fw-bold font-12 "
                                      style={{
                                        color:
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      Amount Payable
                                    </span>
                                    <span
                                      className="fw-bold font-12 "
                                      style={{
                                        color:
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      {documentConfig?.balanceDue || 0}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between p-0 px-2">
                                    <span className="font-12">Amount Paid</span>
                                    <span className="font-12 ">
                                      
                                      {documentConfig?.paidAmount}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                          <div className="row mt-2">
                            <div className="col-4 p-2 pt-0  border-dark font-11">
                              {documentConfig?.bankDetails && (
                                   <>
                            <strong>Bank Details:</strong>                            
                              {documentConfig?.bankDetails?.accountNo && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Account #:</span>
                                  {documentConfig.bankDetails.accountNo}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.ifsc && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>IFSC:</span>
                                  {documentConfig.bankDetails.ifsc}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.beneficiaryName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Name:</span>
                                  {documentConfig.bankDetails.beneficiaryName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.bankName && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Bank Name:</span>
                                  {documentConfig.bankDetails.bankName}
                                </div>
                              )}

                              {documentConfig?.bankDetails?.branch && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>Branch Name:</span>
                                  {documentConfig.bankDetails.branch}
                                </div>
                              )}

                              {documentConfig?.upiId && (
                                <div className="d-flex font-11">
                                  <span style={{ width: "80px" }}>UPI ID:</span>
                                  {documentConfig?.upiId}
                                </div>
                              )}
                          </>
                                )}
                            </div>
                            {qrCodeDataUrl && (
                              <div className="col-3 d-flex align-items-center justify-content-end">
                                <img
                                  src={qrCodeDataUrl}
                                  alt="QR"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                  }}
                                />
                              </div>
                            )}

                            <div className="col-5  ms-auto">
                              <div className="text-end p-0 fw-bold font-12 mt-2 ">
                                {documentConfig?.fromData?.title}
                              </div>
                              <div>
                                {storeSettings?.invoiceSettings?.signature ? (
                                  <div className="text-end p-0 fw-bold pb-1">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={100}
                                    />
                                  </div>
                                ) : (
                                  <div className="p-0 pt-4">
                                    <hr
                                      className="m-1 ms-auto "
                                      style={{
                                        border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                        maxWidth: "120px",
                                      }}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0 font-12">
                                  Authorized Signatory
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              {documentConfig?.remarks && (
                                <table width="100%" className="mb-2">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div className="font-12 fw-600 align-left text-black mb-1">
                                          Remark
                                        </div>

                                        <div
                                          className="font-12 p-0"
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            minHeight: "30px",
                                          }}
                                        >
                                          {capitalizeFirstLetter(
                                            documentConfig?.remarks,
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                            <div className="col-12 ">
                              {terms?.length > 0 && !isPreSale && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td
                                        style={{
                                          padding: "0px",
                                        }}
                                        width="100%"
                                      >
                                        <div
                                          className="font-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-12">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <>
                              <div className="mt-2 font-11 d-flex justify-content-left ">
                                <span>
                                  Powered By{" "}
                                  <span className="fw-bold">NegoBill.com</span>
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
            
              </>
            ) : (
              <div className="default-content">
                <p>No content available for this sale mode.</p>
              </div>
            )}
          
    </>
  );
};

export default PrimeInvoice;
